import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface PreparationChecklistSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ChecklistItem {
  label: string;
  description: string;
}

interface ChecklistCategory {
  id: string;
  title: string;
  items: ChecklistItem[];
}

const CHECKLIST_CATEGORIES: ChecklistCategory[] = [
  {
    id: "financial",
    title: "Financial & Company Basics",
    items: [
      {
        label: "Financial Statements or Management Accounts",
        description:
          "Needed for balance sheet size and total turnover (in EUR or local currency with conversion basis).",
      },
      {
        label: "Company Registration Documents",
        description:
          "To confirm legal form (e.g., Private limited, sole proprietorship) and NACE/sector codes.",
      },
      {
        label: "Asset Registers & Leases",
        description:
          "For geolocation of owned/managed sites and identifying assets exposed to climate risks.",
      },
    ],
  },
  {
    id: "hr",
    title: "Employee & HR Data",
    items: [
      {
        label: "HR Registers & Payroll Data",
        description:
          "Required for total headcount/FTE, breakdown of permanent vs. temporary employees, and gender split data.",
      },
      {
        label: "Wage & Compensation Records",
        description:
          "To confirm minimum wage compliance and calculate the gender pay gap.",
      },
      {
        label: "Health & Safety (HSE) Logs",
        description:
          "Accident registers, injury/fatality records, and records of hours worked to calculate the accident rate.",
      },
      {
        label: "Training Registers",
        description:
          "Attendance sheets to calculate total and average training hours per employee, split by gender.",
      },
      {
        label: "Contracts & Agreements",
        description:
          "To check the percentage of employees covered by collective bargaining agreements.",
      },
    ],
  },
  {
    id: "utilities",
    title: "Utility Bills",
    items: [
      {
        label: "Electricity Bills & Meter Readings",
        description:
          "To calculate total electricity consumption (needs conversion to MWh).",
      },
      {
        label: "Water Bills",
        description:
          "To measure total water withdrawal and discharge (in m³).",
      },
      {
        label: "Renewable Energy Documentation",
        description:
          "Green tariffs, supplier mix data, or guarantees of origin to determine the renewable share.",
      },
    ],
  },
  {
    id: "fuel",
    title: "Fuel & Transport Records",
    items: [
      {
        label: "Fuel Invoices & Logs",
        description:
          "Purchasing records for diesel, petrol, LPG, gas, or biomass used in facilities and company vehicles.",
      },
      {
        label: "Generator & Vehicle Logs",
        description: "To help calculate Scope 1 direct emissions.",
      },
      {
        label: "Travel & Expense Records (If using Comprehensive Scope 3)",
        description:
          "Flight records, distance traveled (km), and hotel nights for business travel, plus freight/logistics delivery records.",
      },
    ],
  },
  {
    id: "waste",
    title: "Waste & Environmental Data",
    items: [
      {
        label: "Waste Contractor Reports",
        description:
          "Invoices or logs detailing non-hazardous and hazardous waste generated, diverted (recycled/reused), or disposed of (in kg or tonnes).",
      },
      {
        label: "Pollution & Discharge Records",
        description:
          "Wastewater lab results, emission monitoring reports, and environmental permits for air, water, or soil pollutants.",
      },
      {
        label: "Material Purchase Records",
        description:
          "Data on significant materials used, including packaging records.",
      },
      {
        label: "Site Maps & Environmental Studies",
        description:
          "To identify total land use (hectares), sealed areas, nature-oriented areas, and proximity to biodiversity-sensitive sites.",
      },
    ],
  },
  {
    id: "governance",
    title: "Policies, Governance & Certifications",
    items: [
      {
        label: "Sustainability Certifications",
        description:
          "Audit reports and certificates like ISO 14001, ISO 45001, HACCP, or B Corp.",
      },
      {
        label: "Corporate Policies",
        description:
          "Copies of Health & Safety procedures, Code of Conduct, Anti-bribery/Whistleblowing policies, and HR policies (covering discrimination, child labor, and forced labor).",
      },
      {
        label: "Legal & Compliance Registers",
        description:
          "To confirm the number of corruption/bribery convictions or fines incurred during the reporting period.",
      },
      {
        label: "Governance Records",
        description:
          "Board or supervisory board records to determine the gender diversity ratio at the management level.",
      },
      {
        label: "Strategy Documents",
        description:
          "Information mapping out main products/services, target markets, supplier codes of conduct, and climate transition plans or targets.",
      },
    ],
  },
];

function downloadCsv() {
  const rows = [["Category", "Item", "Description"]];
  CHECKLIST_CATEGORIES.forEach((cat) => {
    cat.items.forEach((item) => {
      rows.push([cat.title, item.label, item.description]);
    });
  });
  const csv = rows
    .map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "pre-start-checklist.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export function PreparationChecklistSheet({
  open,
  onOpenChange,
}: PreparationChecklistSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="z-[200] w-[90vw] sm:w-[500px] flex flex-col p-0 overflow-hidden"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100 flex-shrink-0">
          <SheetTitle className="text-lg font-bold text-brand-accent">
            Pre-Start Checklist
          </SheetTitle>
          <SheetDescription className="text-sm text-slate-500">
            Gather these documents before filling in the assessment to avoid
            interruptions.
          </SheetDescription>
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <span>Download:</span>
            <a
              href="/checklist.pdf"
              download="pre-start-checklist.pdf"
              className="text-brand-accent hover:underline"
            >
              pdf
            </a>
            <span>·</span>
            <button
              onClick={downloadCsv}
              className="text-brand-accent hover:underline cursor-pointer"
            >
              csv
            </button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <Accordion type="single" collapsible className="w-full">
            {CHECKLIST_CATEGORIES.map((category) => (
              <AccordionItem key={category.id} value={category.id}>
                <AccordionTrigger className="text-sm font-semibold text-slate-800 hover:no-underline">
                  {category.title}
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="flex flex-col gap-3 pb-2">
                    {category.items.map((item, index) => (
                      <li key={`${category.id}-${index}`} className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-slate-700">
                          {item.label}
                        </span>
                        <span className="text-xs text-slate-500">
                          {item.description}
                        </span>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </SheetContent>
    </Sheet>
  );
}
